import React, { useMemo } from "react";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import color from "../../../Constants/colors";

interface Props {
  children: React.ReactNode;
}

const VoicesThemeProvider: React.FC<Props> = ({ children }) => {
  const appTheme = useTheme();

  const voicesTheme = useMemo(() => {
    const fixedSize = (fontSize: string, lineHeight: string, textColor: string) => {
      const size = { fontSize, lineHeight };
      return {
        ...size,
        color: textColor,
        [appTheme.breakpoints.down("lg")]: size,
        [appTheme.breakpoints.down("sm")]: size,
      };
    };

    return createTheme(appTheme, {
      typography: {
        h1: { color: color.gray900 },
        h2: { color: color.gray900 },
        h3: { color: color.gray900 },
        h4: { color: color.gray900 },
        h5: { color: color.gray900 },
        h6: { color: color.gray900 },
        subtitle1: fixedSize("0.95rem", "1.4", color.gray900),
        subtitle2: fixedSize("0.875rem", "1.4", color.gray900),
        body1: fixedSize("0.95rem", "1.5", color.gray900),
        body2: fixedSize("0.875rem", "1.45", color.gray900),
        caption: fixedSize("0.8rem", "1.4", color.gray600),
      },
      components: {
        MuiTableCell: {
          styleOverrides: {
            root: {
              color: color.gray900,
              borderColor: color.gray200,
              fontSize: "0.875rem",
              padding: "10px 12px",
            },
            head: {
              color: color.gray700,
              fontWeight: 600,
              fontSize: "0.8rem",
              backgroundColor: color.gray50,
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            label: { fontSize: "0.78rem" },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              color: color.gray900,
              backgroundColor: color.white,
              ".MuiOutlinedInput-notchedOutline": { borderColor: color.gray300 },
              input: { color: color.gray900 },
              textarea: { color: color.gray900 },
              "&.Mui-disabled": {
                backgroundColor: color.gray50,
                "input, textarea": {
                  color: color.gray700,
                  WebkitTextFillColor: color.gray700,
                },
                ".MuiOutlinedInput-notchedOutline": { borderColor: color.gray200 },
              },
            },
          },
        },
        MuiInputBase: {
          styleOverrides: {
            root: {
              "&.Mui-disabled": {
                color: `${color.gray700} !important`,
                "& .MuiSelect-select": {
                  color: `${color.gray700} !important`,
                  WebkitTextFillColor: `${color.gray700} !important`,
                },
              },
            },
          },
        },
        MuiSelect: {
          styleOverrides: {
            root: {
              color: color.gray900,
              "& .MuiOutlinedInput-notchedOutline": { borderColor: color.gray300 },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: color.gray500 },
            },
            icon: { color: color.gray500 },
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: {
              color: `${color.gray600} !important`,
            },
          },
        },
        MuiFormHelperText: {
          styleOverrides: {
            root: { color: color.gray500 },
          },
        },
        MuiFormControlLabel: {
          styleOverrides: {
            label: { color: color.gray900 },
          },
        },
        MuiMenu: {
          styleOverrides: {
            paper: {
              backgroundColor: color.white,
              border: `1px solid ${color.gray200}`,
              borderLeft: `4px solid ${color.orange500}`,
              "& .MuiMenuItem-root": {
                color: color.gray900,
                "&:hover": { backgroundColor: color.orange50 },
                "&.Mui-selected, &.Mui-selected:hover": {
                  backgroundColor: color.orange50,
                  color: color.orange600,
                },
              },
            },
          },
        },
        MuiListSubheader: {
          styleOverrides: {
            root: {
              color: color.gray600,
              backgroundColor: color.white,
              fontWeight: 600,
              fontSize: "0.75rem",
              lineHeight: "32px",
            },
          },
        },
        MuiAutocomplete: {
          styleOverrides: {
            paper: { backgroundColor: color.white, color: color.gray900 },
            option: { color: color.gray900 },
            noOptions: { color: color.gray500 },
            popupIndicator: { color: color.gray500 },
            clearIndicator: { color: color.gray500 },
          },
        },
        MuiIconButton: {
          styleOverrides: {
            root: { color: color.gray700 },
          },
        },
        MuiToggleButton: {
          styleOverrides: {
            root: { color: color.gray700 },
          },
        },
        MuiTableSortLabel: {
          styleOverrides: {
            root: {
              color: `${color.gray700} !important`,
              "& .MuiTableSortLabel-icon": { color: `${color.gray500} !important` },
            },
          },
        },
        MuiTablePagination: {
          styleOverrides: {
            root: { color: color.gray700 },
            selectIcon: { color: color.gray500 },
          },
        },
      },
    });
  }, [appTheme]);

  return <ThemeProvider theme={voicesTheme}>{children}</ThemeProvider>;
};

export default VoicesThemeProvider;
